import type { ApiApplyRequest, ApiTaskProposal } from './ai.contract';
import type { ApplyResult, QuickTaskProposal } from '../types';

// FIXTURE: unverified against backend — committed to drive the UI contract
// while the proposal endpoint is unavailable (kept until Step 8).

/** Happy path: every field model-proposed with concrete values, no ambiguity. */
export const quickTaskProposalFixture: QuickTaskProposal = {
  correlationId: 'fixture-clean-1',
  tags: [{ id: 'tag-design', name: 'tag-design', isNew: false, color: null }],
  fields: {
    title: { value: 'Revisar propuesta de diseño', provenance: 'model' },
    description: {
      value: 'Revisar el flujo propuesto y dejar comentarios antes del viernes.',
      provenance: 'model',
    },
    dueDate: { value: '2026-08-20', provenance: 'model' },
    startDate: { value: null, provenance: 'notSupplied' },
    statusId: { value: 'status-in-progress', provenance: 'model' },
    assigneeId: {
      value: '550e8400-e29b-41d4-a716-446655440000',
      provenance: 'model',
      candidates: [],
    },
    groupId: { value: 'group-1', provenance: 'model' },
    priority: { value: 'high', provenance: 'model' },
    estimatedMinutes: { value: null, provenance: 'notSupplied' },
    tags: { value: 'tag-design', provenance: 'model', candidates: [] },
  },
  observations: ['La tarea se agrega al estado "En progreso" del tablero actual.'],
};

/** Ambiguous assignee: value null + two candidates → un-picked Select. */
export const quickTaskProposalAmbiguousFixture: QuickTaskProposal = {
  correlationId: 'fixture-ambiguous-1',
  tags: [],
  fields: {
    title: { value: 'Asignar revisión de diseño', provenance: 'model' },
    description: { value: null, provenance: 'notSupplied' },
    dueDate: { value: null, provenance: 'notSupplied' },
    startDate: { value: null, provenance: 'notSupplied' },
    statusId: { value: 'status-review', provenance: 'context' },
    assigneeId: {
      value: null,
      provenance: 'model',
      candidates: [
        { id: '550e8400-e29b-41d4-a716-446655440000', name: 'Ana Torres' },
        { id: '6ba7b810-9dad-11d1-80b4-00c04fd430c8', name: 'Luis Pérez' },
      ],
    },
    groupId: { value: null, provenance: 'notSupplied' },
    priority: { value: null, provenance: 'notSupplied' },
    estimatedMinutes: { value: null, provenance: 'notSupplied' },
    tags: { value: null, provenance: 'notSupplied' },
  },
  observations: ['No pude determinar el responsable: elegí una opción de la lista.'],
};

/** New status: value is a board-wide creation, with alternatives offered. */
export const quickTaskProposalWithNewStatusFixture: QuickTaskProposal = {
  correlationId: 'fixture-new-status-1',
  tags: [],
  fields: {
    title: { value: 'Definir proceso de QA', provenance: 'model' },
    description: {
      value: 'Definir criterios de aceptación y responsables del proceso.',
      provenance: 'model',
    },
    dueDate: { value: null, provenance: 'notSupplied' },
    startDate: { value: null, provenance: 'notSupplied' },
    statusId: {
      value: 'QA',
      provenance: 'model',
      isNew: true,
      candidates: [
        { id: 'status-in-progress', name: 'En progreso' },
        { id: 'status-review', name: 'En revisión' },
      ],
    },
    assigneeId: { value: null, provenance: 'notSupplied' },
    groupId: { value: null, provenance: 'notSupplied' },
    priority: { value: 'medium', provenance: 'context' },
    estimatedMinutes: { value: null, provenance: 'notSupplied' },
    tags: { value: null, provenance: 'notSupplied' },
  },
  observations: ['El estado "QA" no existe: se creará en todo el tablero.'],
};

/**
 * Builds a deterministic proposal from a free-text instruction.
 * Keyword routing keeps the three fixture variants reachable in mock mode:
 * "estado" → new status, "asign" → ambiguous assignee, otherwise happy path.
 */
export function mockQuickTaskRequest(instruction: string): QuickTaskProposal {
  const normalized = instruction.toLowerCase();
  if (normalized.includes('estado')) {
    return withCorrelation(quickTaskProposalWithNewStatusFixture, instruction);
  }
  if (normalized.includes('asign')) {
    return withCorrelation(quickTaskProposalAmbiguousFixture, instruction);
  }
  return withCorrelation(quickTaskProposalFixture, instruction);
}

/** Simulates a created task in mock mode. Id is deterministic per title+correlation. */
export function mockQuickTaskApply(request: ApiApplyRequest): ApplyResult {
  return { id: `task-${slugify(`${request.title} ${request.correlationId ?? ''}`)}` };
}

// ---------------------------------------------------------------------------
// Wire-shaped fixtures for integration tests
// ---------------------------------------------------------------------------

/** Wire-shaped clean fixture for integration tests. */
export const quickTaskProposalWireFixture: ApiTaskProposal = {
  correlationId: 'fixture-clean-1',
  title: { value: 'Revisar propuesta de diseño', provenance: 'model-suggested' },
  description: {
    value: 'Revisar el flujo propuesto y dejar comentarios antes del viernes.',
    provenance: 'model-suggested',
  },
  status: {
    id: 'status-in-progress',
    name: 'En progreso',
    isNew: false,
    provenance: 'model-suggested',
    candidates: [],
  },
  group: { id: null, name: null, isNew: false, provenance: 'not-supplied', candidates: [] },
  assignee: {
    id: '550e8400-e29b-41d4-a716-446655440000',
    name: 'Ana Torres',
    isNew: false,
    provenance: 'model-suggested',
    candidates: [],
  },
  priority: { value: 3, provenance: 'model-suggested' },
  estimatedMinutes: { value: null, provenance: 'not-supplied' },
  dueDate: {
    value: { date: '2026-08-20', time: null, timeZoneId: null },
    provenance: 'model-suggested',
  },
  startDate: { value: null, provenance: 'not-supplied' },
  tags: [{ id: 'tag-design', name: 'Diseño', isNew: false, provenance: 'model-suggested' }],
  observations: ['La tarea se agrega al estado "En progreso" del tablero actual.'],
  newEntities: [],
  ambiguities: [],
};

/** Wire-shaped new-status fixture for integration tests. */
export const quickTaskProposalNewStatusWireFixture: ApiTaskProposal = {
  correlationId: 'fixture-new-status-1',
  title: { value: 'Definir proceso de QA', provenance: 'model-suggested' },
  description: {
    value: 'Definir criterios de aceptación y responsables del proceso.',
    provenance: 'model-suggested',
  },
  // A column the board does not have, with no candidates. The server resolves a name either
  // way: matched-but-ambiguous comes back with candidates and `isNew: false`, unmatched comes
  // back `isNew: true` with none — never both, so a fixture claiming both described a response
  // no caller could ever receive, and left the only editor on screen with nothing selected.
  status: {
    id: null,
    name: 'QA',
    isNew: true,
    provenance: 'model-suggested',
    color: 'purple-iris',
    candidates: [],
  },
  group: { id: null, name: null, isNew: false, provenance: 'not-supplied', candidates: [] },
  assignee: { id: null, name: null, isNew: false, provenance: 'not-supplied', candidates: [] },
  priority: { value: 2, provenance: 'from-context' },
  estimatedMinutes: { value: null, provenance: 'not-supplied' },
  dueDate: { value: null, provenance: 'not-supplied' },
  startDate: { value: null, provenance: 'not-supplied' },
  tags: [],
  observations: ['El estado "QA" no existe: se creará en todo el tablero.'],
  newEntities: [{ type: 'status', name: 'QA' }],
  ambiguities: [],
};

function withCorrelation(fixture: QuickTaskProposal, instruction: string): QuickTaskProposal {
  return { ...fixture, correlationId: `fixture-${slugify(instruction)}` };
}

function slugify(value: string): string {
  const cleaned = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return cleaned.slice(0, 24) || 'empty';
}
